import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { isNative, pickOrCapturePhoto, hapticSuccess, openExternal } from '../native';
import {
  Users, MessageSquare, Calendar, Send, Loader2,
  Plus, Trash2, Video, Image as ImageIcon, Link as LinkIcon,
  CalendarPlus, Check, X, HelpCircle, Download, BarChart3, ArrowRight,
  Upload, Camera
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Team Messages Tab
const TeamMessages = ({ teamId, token }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaType, setMediaType] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, [teamId]);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/teams/${teamId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/teams/${teamId}/messages`, {
        team_id: teamId,
        content: newMessage,
        media_url: mediaUrl || null,
        media_type: mediaType || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage('');
      setMediaUrl('');
      setMediaType('');
      fetchMessages();
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getYouTubeEmbed = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^&\n?#]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const getTikTokEmbed = (url) => {
    if (!url) return null;
    const match = url.match(/tiktok\.com\/@[\w.-]+\/video\/(\d+)/);
    return match ? `https://www.tiktok.com/embed/v2/${match[1]}` : null;
  };

  if (loading) {
    return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;
  }

  return (
    <div className="space-y-4">
      {/* Message Input */}
      <Card>
        <CardContent className="pt-4">
          <Textarea
            placeholder="Write a message to your team..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            className="mb-4"
            data-testid="message-input"
          />
          
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="Add video/image URL (YouTube, TikTok, etc.)"
              value={mediaUrl}
              onChange={(e) => setMediaUrl(e.target.value)}
              className="flex-1"
            />
            <select
              className="border rounded px-3"
              value={mediaType}
              onChange={(e) => setMediaType(e.target.value)}
            >
              <option value="">Type</option>
              <option value="video">Video</option>
              <option value="image">Image</option>
              <option value="link">Link</option>
            </select>
          </div>

          <Button onClick={handleSend} disabled={sending || !newMessage.trim()} data-testid="send-message-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Send Message
          </Button>
        </CardContent>
      </Card>

      {/* Messages List */}
      <div className="space-y-4">
        {messages.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No messages yet. Start the conversation!</p>
            </CardContent>
          </Card>
        ) : (
          messages.map((msg) => (
            <Card key={msg.id} data-testid={`message-${msg.id}`}>
              <CardContent className="pt-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className="font-semibold">{msg.sender_name}</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      {new Date(msg.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
                <p className="whitespace-pre-wrap">{msg.content}</p>
                
                {/* Media Embed */}
                {msg.media_url && (
                  <div className="mt-4">
                    {msg.media_type === 'video' && getYouTubeEmbed(msg.media_url) && (
                      <div className="aspect-video">
                        <iframe
                          src={getYouTubeEmbed(msg.media_url)}
                          className="w-full h-full rounded-lg"
                          allowFullScreen
                        />
                      </div>
                    )}
                    {msg.media_type === 'video' && getTikTokEmbed(msg.media_url) && (
                      <div className="max-w-sm mx-auto">
                        <iframe
                          src={getTikTokEmbed(msg.media_url)}
                          className="w-full rounded-lg"
                          style={{ height: '600px' }}
                          allowFullScreen
                        />
                      </div>
                    )}
                    {msg.media_type === 'image' && (
                      <img src={msg.media_url} alt="Shared" className="max-w-full rounded-lg" />
                    )}
                    {msg.media_type === 'link' && (
                      <button
                        type="button"
                        onClick={() => openExternal(msg.media_url)}
                        className="text-primary underline flex items-center gap-1"
                      >
                        <Link className="h-4 w-4" />
                        {msg.media_url}
                      </button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Team Events Tab
const TeamEvents = ({ teamId, token }) => {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    event_type: 'practice',
    description: '',
    location: '',
    start_time: '',
    end_time: ''
  });

  useEffect(() => {
    fetchEvents();
  }, [teamId]);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/teams/${teamId}/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(res.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.title || !form.start_time || !form.end_time) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/teams/${teamId}/events`, {
        team_id: teamId,
        ...form
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event created');
      setShowForm(false);
      setForm({ title: '', event_type: 'practice', description: '', location: '', start_time: '', end_time: '' });
      fetchEvents();
    } catch (error) {
      toast.error('Failed to create event');
    } finally {
      setSaving(false);
    }
  };

  const handleExportCalendar = () => {
    openExternal(`${API}/teams/${teamId}/events/ical`);
  };

  if (loading) {
    return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Team Events</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCalendar}>
            <Download className="h-4 w-4 mr-2" />
            Export Calendar
          </Button>
          <Button size="sm" onClick={() => setShowForm(!showForm)} data-testid="add-event-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Event
          </Button>
        </div>
      </div>

      {/* Create Event Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create Event</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({...form, title: e.target.value})}
                  placeholder="Practice Session"
                  data-testid="event-title-input"
                />
              </div>
              <div>
                <Label>Type</Label>
                <select
                  className="w-full border rounded-md h-10 px-3"
                  value={form.event_type}
                  onChange={(e) => setForm({...form, event_type: e.target.value})}
                >
                  <option value="practice">Practice</option>
                  <option value="meeting">Team Meeting</option>
                  <option value="social">Social Event</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                placeholder="Event details..."
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({...form, location: e.target.value})}
                placeholder="Field A, Community Park"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time *</Label>
                <Input
                  type="datetime-local"
                  value={form.start_time}
                  onChange={(e) => setForm({...form, start_time: e.target.value})}
                  data-testid="event-start-input"
                />
              </div>
              <div>
                <Label>End Time *</Label>
                <Input
                  type="datetime-local"
                  value={form.end_time}
                  onChange={(e) => setForm({...form, end_time: e.target.value})}
                  data-testid="event-end-input"
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex gap-2">
            <Button onClick={handleCreate} disabled={saving} data-testid="save-event-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Event
            </Button>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
          </CardFooter>
        </Card>
      )}

      {/* Events List */}
      {events.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No upcoming events scheduled.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <EventCard key={event.id} event={event} token={token} onUpdate={fetchEvents} />
          ))}
        </div>
      )}
    </div>
  );
};

// Event Card with RSVP
const EventCard = ({ event, token, onUpdate }) => {
  const [rsvps, setRsvps] = useState(null);
  const [myRsvp, setMyRsvp] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRsvps();
  }, [event.id]);

  const fetchRsvps = async () => {
    try {
      const res = await axios.get(`${API}/events/${event.id}/rsvps`);
      setRsvps(res.data);
    } catch (error) {
      console.error('Error fetching RSVPs:', error);
    }
  };

  const handleRsvp = async (status) => {
    setSaving(true);
    try {
      await axios.post(`${API}/events/${event.id}/rsvp`, {
        event_id: event.id,
        status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyRsvp(status);
      fetchRsvps();
      toast.success('RSVP updated');
    } catch (error) {
      toast.error('Failed to update RSVP');
    } finally {
      setSaving(false);
    }
  };

  const getEventTypeColor = () => {
    switch (event.event_type) {
      case 'practice': return 'bg-blue-500';
      case 'meeting': return 'bg-purple-500';
      case 'social': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <Card data-testid={`event-${event.id}`}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div>
            <Badge className={getEventTypeColor()}>{event.event_type}</Badge>
            <CardTitle className="mt-2">{event.title}</CardTitle>
          </div>
          {rsvps && (
            <div className="text-right text-sm">
              <div className="flex items-center gap-4">
                <span className="text-green-600">{rsvps.going} going</span>
                <span className="text-yellow-600">{rsvps.maybe} maybe</span>
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 text-sm">
          <p><strong>When:</strong> {new Date(event.start_time).toLocaleString()}</p>
          {event.location && <p><strong>Where:</strong> {event.location}</p>}
          {event.description && <p className="text-muted-foreground">{event.description}</p>}
        </div>
        
        {/* RSVP Buttons */}
        <div className="mt-4 flex gap-2">
          <Button
            size="sm"
            variant={myRsvp === 'going' ? 'default' : 'outline'}
            className={myRsvp === 'going' ? 'bg-green-500 hover:bg-green-600' : ''}
            onClick={() => handleRsvp('going')}
            disabled={saving}
            data-testid={`rsvp-going-${event.id}`}
          >
            <Check className="h-4 w-4 mr-1" />
            Going
          </Button>
          <Button
            size="sm"
            variant={myRsvp === 'maybe' ? 'default' : 'outline'}
            className={myRsvp === 'maybe' ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
            onClick={() => handleRsvp('maybe')}
            disabled={saving}
            data-testid={`rsvp-maybe-${event.id}`}
          >
            <HelpCircle className="h-4 w-4 mr-1" />
            Maybe
          </Button>
          <Button
            size="sm"
            variant={myRsvp === 'not_going' ? 'default' : 'outline'}
            className={myRsvp === 'not_going' ? 'bg-red-500 hover:bg-red-600' : ''}
            onClick={() => handleRsvp('not_going')}
            disabled={saving}
            data-testid={`rsvp-not-going-${event.id}`}
          >
            <X className="h-4 w-4 mr-1" />
            Cannot Go
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Team Management Page
export const TeamManagement = () => {
  const { teamId } = useParams();
  const { token, user } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    fetchTeam();
  }, [teamId]);

  const fetchTeam = async () => {
    try {
      const res = await axios.get(`${API}/teams/${teamId}`);
      setTeam(res.data);
    } catch (error) {
      console.error('Error fetching team:', error);
      toast.error('Failed to load team');
    } finally {
      setLoading(false);
    }
  };

  const uploadLogoFile = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/teams/${teamId}/upload-logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update the team state with the new logo URL
      setTeam(prev => ({ ...prev, logo_url: response.data.logo_url }));
      hapticSuccess();
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoUpload = (e) => uploadLogoFile(e.target.files?.[0]);

  const handleLogoTrigger = async () => {
    if (isNative()) {
      const file = await pickOrCapturePhoto({ filename: 'team-logo.jpg' });
      if (file) await uploadLogoFile(file);
    } else {
      fileInputRef.current?.click();
    }
  };

  const isTeamOwner = team && user && (team.created_by === user.id || user.role === 'admin');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <p>Team not found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center gap-4 mb-8">
        <div className="relative group">
          {team.logo_url ? (
            <img src={team.logo_url} alt={team.name} className="w-16 h-16 rounded-lg object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
              <Users className="w-8 h-8 text-muted-foreground" />
            </div>
          )}
          {isTeamOwner && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                className="hidden"
                onChange={handleLogoUpload}
                data-testid="logo-upload-input"
              />
              <button
                onClick={handleLogoTrigger}
                disabled={uploadingLogo}
                className="absolute inset-0 bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                data-testid="upload-logo-btn"
              >
                {uploadingLogo ? (
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                ) : (
                  <Camera className="w-5 h-5 text-white" />
                )}
              </button>
            </>
          )}
        </div>
        <div>
          <h1 className="text-3xl font-bold">{team.name}</h1>
          <p className="text-muted-foreground">
            {team.age_category} - {team.gender}
          </p>
          {isTeamOwner && !team.logo_url && (
            <button
              onClick={handleLogoTrigger}
              className="text-xs text-primary hover:underline mt-1"
            >
              + Add team logo
            </button>
          )}
        </div>
      </div>

      <Tabs defaultValue="messages" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="messages" className="flex items-center gap-2" data-testid="tab-messages">
            <MessageSquare className="h-4 w-4" />
            Messages
          </TabsTrigger>
          <TabsTrigger value="events" className="flex items-center gap-2" data-testid="tab-events">
            <Calendar className="h-4 w-4" />
            Events & RSVP
          </TabsTrigger>
          <TabsTrigger value="roster" className="flex items-center gap-2" data-testid="tab-roster">
            <Users className="h-4 w-4" />
            Roster
          </TabsTrigger>
        </TabsList>

        <TabsContent value="messages">
          <TeamMessages teamId={teamId} token={token} />
        </TabsContent>

        <TabsContent value="events">
          <TeamEvents teamId={teamId} token={token} />
        </TabsContent>

        <TabsContent value="roster">
          <Card>
            <CardHeader>
              <CardTitle>Team Roster</CardTitle>
              <CardDescription>Manage your team members and player information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                View and manage your complete team roster including player positions, contact info, and season history.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to={`/teams/${teamId}/roster`}>
                  <Button className="gap-2" data-testid="manage-roster-btn">
                    <Users className="h-4 w-4" />
                    Manage Roster
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to={`/teams/${teamId}/analytics`}>
                  <Button variant="outline" className="gap-2" data-testid="view-analytics-btn">
                    <BarChart3 className="h-4 w-4" />
                    Team Analytics
                  </Button>
                </Link>
                <Link to={`/teams/${teamId}/performance`}>
                  <Button variant="outline" className="gap-2" data-testid="view-stats-btn">
                    <BarChart3 className="h-4 w-4" />
                    Performance Charts
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
